/* eslint-disable no-underscore-dangle */
/* eslint-disable react/destructuring-assignment */
import { google as Google } from '@alugha/ima'
import React, { Requireable } from 'react'
import { ImaService } from './ImaService'

declare global {
  interface Window {
    google: any
    webapis: any
    VIZIO: any
  }
}
export interface SponsorshipProps {
  adTagUrl: string
  onAdCompleted: () => void
  onAllAdsCompleted: () => void
  onAdsManagerLoadedError: (onAdError: any) => void
  onAdError: (onAdError: any) => void
  onAdLoaded: (onAdLoaded: any) => void
  onAdPaused: () => void
  onAdProgress: (onAdProgress: any) => void
  onAdResume: () => void
  onAdStarted: () => void
  onAdFirstQuartile: () => void
  onAdMidPoint: () => void
  onAdThirdQuartile: () => void
  onContentPauseRequested: () => void
  onContentResumeRequested: () => void
  onInitializeIMAError: (initializeIMAError: any) => void
  playerRef: React.RefObject<HTMLVideoElement>
}

export class Sponsorship extends React.Component<SponsorshipProps> {
  // eslint-disable-next-line react/static-property-placement
  static propTypes: {
    adTagUrl: Requireable<string>
    onAdCompleted: Requireable<() => void>
    onAllAdsCompleted: Requireable<() => void>
    onAdsManagerLoadedError: Requireable<(onAdError: any) => void>
    onAdError: Requireable<(onAdError: any) => void>
    onAdLoaded: Requireable<(onAdLoaded: any) => void>
    onAdPaused: Requireable<() => void>
    onAdProgress: Requireable<(onAdProgress: any) => void>
    onAdResume: Requireable<() => void>
    onAdStarted: Requireable<() => void>
    onAdFirstQuartile: Requireable<() => void>
    onAdMidPoint: Requireable<() => void>
    onAdThirdQuartile: Requireable<() => void>
    onContentPauseRequested: Requireable<() => void>
    onContentResumeRequested: Requireable<() => void>
    onInitializeIMAError: Requireable<(initializeIMAError: any) => void>
    playerRef: Requireable<React.RefObject<HTMLVideoElement>>
  }

  private imaService: ImaService
  private _isMounted = false
  adContainerRef: React.RefObject<HTMLDivElement>
  adDisplayContainer!: Google.ima.AdDisplayContainer
  adsLoader!: Google.ima.AdsLoader
  adsManager!: Google.ima.AdsManager
  height: number
  width: number
  allAdsCompletedInvoked: boolean

  constructor(props: SponsorshipProps) {
    super(props)
    this.imaService = new ImaService()
    // @ts-ignore
    this.width = 1920
    // @ts-ignore
    this.height = 1080
    this.adContainerRef = React.createRef()
    this.allAdsCompletedInvoked = false
  }

  componentDidMount() {
    this._isMounted = true
    this.initializeIMA()
  }

  componentWillUnmount() {
    this._isMounted = false
    this.destroy()
  }

  async onAdsManagerLoaded(
    adsManagerLoadedEvent: Google.ima.AdsManagerLoadedEvent
  ) {
    const adsRenderingSettings = await this.imaService.AdsRenderingSettings()
    const eventTypes = await this.imaService.getEventTypes()

    adsRenderingSettings.enablePreloading = false
    adsRenderingSettings.mimeTypes = ['video/mp4']
    adsRenderingSettings.bitrate = 512
    adsRenderingSettings.loadVideoTimeout = 12000
    // Instantiate the AdsManager from the adsLoader response and pass it the video element
    const currentPlayerRef = this.props.playerRef.current

    if (currentPlayerRef == null) {
      this.props.onAllAdsCompleted()
      return
    }

    this.adsManager = adsManagerLoadedEvent.getAdsManager(
      currentPlayerRef,
      adsRenderingSettings
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.LOADED,
      this.onAdLoaded.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.PAUSED,
      this.onAdPaused.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.RESUMED,
      this.onAdResume.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.STARTED,
      this.onAdStarted.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.FIRST_QUARTILE,
      this.onAdFirstQuartile.bind(this)
    )
    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.MIDPOINT,
      this.onAdMidPoint.bind(this)
    )
    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.THIRD_QUARTILE,
      this.onAdThirdQuartile.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.AD_PROGRESS,
      this.onAdProgress.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.COMPLETE,
      this.onAdCompleted.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdEvent.Type.ALL_ADS_COMPLETED,
      this.onAllAdsCompleted.bind(this)
    )

    this.adsManager.addEventListener(
      eventTypes.AdErrorEvent.Type.AD_ERROR,
      this.onAdManagerError.bind(this)
    )

    this.adDisplayContainer.initialize()

    try {
      this.adsManager.init(this.width, this.height, eventTypes.ViewMode.NORMAL)
      this.adsManager.start()
    } catch (adError) {
      const errorEvent = adError as Google.ima.AdErrorEvent
      this.onAdsManagerLoadedError(errorEvent)
      this.onAdCompleted()
      this.forcingOnAllAdsCompleted()
    }
  }

  onContentPauseRequested() {
    this.props.onContentPauseRequested()
  }

  onContentResumeRequested() {
    this.props.onContentResumeRequested()
  }

  onAdLoaded(adEvent: Google.ima.AdEvent) {
    this.allAdsCompletedInvoked = false
    this.props.onAdLoaded(adEvent)
  }

  onAdPaused() {
    this.props.onAdPaused()
  }

  onAdResume() {
    this.props.onAdResume()
  }

  onAdStarted() {
    this.props.onAdStarted()
  }
  onAdFirstQuartile() {
    this.props.onAdFirstQuartile()
  }
  onAdMidPoint() {
    this.props.onAdMidPoint()
  }
  onAdThirdQuartile() {
    this.props.onAdThirdQuartile()
  }

  onAdProgress(adEvent: Google.ima.AdEvent) {
    this.props.onAdProgress(adEvent)
  }

  onAdsManagerLoadedError(adErrorEvent: Google.ima.AdErrorEvent) {
    this.props.onAdsManagerLoadedError(adErrorEvent.getError())
  }

  onAdCompleted(adEvent?: Google.ima.AdEvent) {
    const adPodInfo = adEvent?.getAd()?.getAdPodInfo()
    const adPosition = adPodInfo?.getAdPosition()
    const totalAds = adPodInfo?.getTotalAds()
    if (!!adPosition && adPosition === totalAds) {
      this.forcingOnAllAdsCompleted()
    }
    this.props.onAdCompleted()
  }

  onAllAdsCompleted() {
    if (!this.allAdsCompletedInvoked) {
      this.forcingOnAllAdsCompleted()
    }
  }

  onAdsLoaderError(adErrorEvent: Google.ima.AdErrorEvent) {
    this.destroy()
    this.props.onAdError(adErrorEvent.getError())
  }

  onAdManagerError(adErrorEvent: Google.ima.AdErrorEvent) {
    this.destroy()
    this.props.onAdError(adErrorEvent.getError())
  }

  forcingOnAllAdsCompleted() {
    this.allAdsCompletedInvoked = true
    this.props.onAllAdsCompleted()
  }

  async initializeIMA() {
    const currentPlayerRef = this.props.playerRef.current
    if (currentPlayerRef === null) {
      this.props.onAllAdsCompleted()
      return
    }

    try {
      await this.imaService.initialize()
      if (!this._isMounted) return

      const eventTypes = await this.imaService.getEventTypes()
      if (!this._isMounted) return

      if (!this.adContainerRef.current) return
      // <video> param not set to avoid tizen errors: OTTERKIDS-1089
      this.adDisplayContainer = await this.imaService.AdDisplayContainer(
        this.adContainerRef.current
      )
      if (!this._isMounted) return

      this.adsLoader = await this.imaService.AdsLoader(this.adDisplayContainer)
      if (!this._isMounted) return

      this.adsLoader.addEventListener(
        eventTypes.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        this.onAdsManagerLoaded.bind(this),
        false
      )

      this.adsLoader.addEventListener(
        eventTypes.AdErrorEvent.Type.AD_ERROR,
        this.onAdsLoaderError.bind(this),
        false
      )

      await this.requestAds()
    } catch (e) {
      if (this._isMounted) {
        setTimeout(() => {
          this.props.onInitializeIMAError(e)
        })
      }
    }
  }

  async requestAds() {
    const adsRequest = await this.imaService.AdsRequest()
    const { adTagUrl } = this.props

    adsRequest.adTagUrl = adTagUrl

    // Un-comment to test Pre-roll, Mid-roll, Post-roll
    // adsRequest.adTagUrl =
    // 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/ad_rule_samples&ciu_szs=300x250&ad_rule=1&impl=s&gdfp_req=1&env=vp&output=vmap&unviewed_position_start=1&cust_params=deployment%3Ddevsite%26sample_ar%3Dpremidpost&cmsid=496&vid=short_onecue&correlator='

    // Specify the linear and nonlinear slot sizes. This helps the SDK to
    // select the correct creative if multiple are returned.
    adsRequest.vastLoadTimeout = 8000
    adsRequest.linearAdSlotWidth = this.width
    adsRequest.linearAdSlotHeight = this.height
    adsRequest.nonLinearAdSlotWidth = this.width
    adsRequest.nonLinearAdSlotHeight = this.height

    this.adsLoader.requestAds(adsRequest)
  }

  destroy() {
    this.adsManager?.destroy()
    this.adDisplayContainer?.destroy()
    this.adsLoader?.destroy()
    this.imaService.destroy()
  }

  render() {
    return <div ref={this.adContainerRef} id="ad-container" />
  }
}
